import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

import type { ProviderDetails, ProviderSkipped } from "@/api/generated/models";
import {
  Button,
  Footer,
  Header,
  Heading,
  Paragraph,
  ScrollView,
} from "@/components";
import {
  EMPTY_PROVIDER,
  ProviderFields,
  providerPayload,
  providerSchema,
  providerValues,
} from "./CareTeamProviderFields";

export const ReferralCollectionScreen = ({
  initialAnswer,
  onContinue,
}: {
  initialAnswer?: ProviderDetails | ProviderSkipped;
  onContinue: (answer: ProviderDetails | ProviderSkipped) => void;
}) => {
  const restored = useMemo(
    () =>
      initialAnswer?.status === "provided"
        ? providerValues(initialAnswer)
        : undefined,
    [initialAnswer],
  );
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(providerSchema),
    defaultValues: EMPTY_PROVIDER,
    values: restored,
    resetOptions: { keepDirtyValues: true },
  });
  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{ padding: "$200", gap: "$250" }}
      >
        <Header gap="$100">
          <Heading size="$xl">Your referring clinician</Heading>
          <Paragraph size="$sm">
            If a clinician referred you to ACCESS, add their details below.
            Otherwise, you can skip this step.
          </Paragraph>
        </Header>
        <ProviderFields
          control={control}
          setValue={setValue}
          errors={errors}
          role="referrer"
        />
      </ScrollView>
      <Footer padding="$200" mode="dialog">
        <Button
          dataTrackingId="access-referrer-continue-button"
          mode="primary"
          constrained
          onPress={handleSubmit((values) =>
            onContinue(providerPayload(values)),
          )}
          disabled={isSubmitting}
        >
          Continue
        </Button>
        <Button
          dataTrackingId="access-referrer-skip-button"
          mode="tertiary"
          onPress={() => onContinue({ status: "skipped" })}
          disabled={isSubmitting}
        >
          Skip for now
        </Button>
      </Footer>
    </>
  );
};
